/**
 * Anfechtungen — Widersprüche gegen BKA/JA/Beschlüsse
 * ===================================================
 * Zeigt alle offenen Widersprüche, Frist-Countdown, Status-Wechsel,
 * Verlinkung zum Ticket-System.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Gavel, Warning, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  open:               { label: "Offen",       cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Warning },
  in_review:          { label: "In Prüfung",  cls: "bg-blue-100 text-blue-800 border-blue-200",    icon: Clock },
  resolved_accepted:  { label: "Stattgegeben", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
  resolved_rejected:  { label: "Abgelehnt",   cls: "bg-slate-200 text-slate-700 border-slate-300", icon: XCircle },
  withdrawn:          { label: "Zurückgezogen", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
};

const TARGET_LABEL = {
  bka_settlement: "BKA-Einzelabrechnung",
  weg_ja: "WEG-Jahresabrechnung",
  weg_owner_abrechnung: "Eigentümer-Abrechnung",
  resolution: "WEG-Beschluss",
};

export default function Anfechtungen() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/challenges${params}`);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  return (
    <div className="space-y-4" data-testid="challenges-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Gavel size={24} className="text-emerald-700" />
            Anfechtungen & Widersprüche
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Widersprüche gegen BKA (§ 556 Abs. 3 BGB), WEG-Jahresabrechnungen (§ 28 WEG)
            und Beschlüsse (§ 45 WEG) mit Frist-Countdown.
          </p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="challenge-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
          Keine Widersprüche für den gewählten Filter.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Betreff</th>
                <th className="text-left px-3 py-2 w-40">Ziel</th>
                <th className="text-left px-3 py-2 w-32">Einreichung</th>
                <th className="text-left px-3 py-2 w-32">Frist</th>
                <th className="text-left px-3 py-2 w-32">Status</th>
                <th className="text-left px-3 py-2 w-28">Quelle</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const s = STATUS_META[r.status] || STATUS_META.open;
                const Icon = s.icon;
                const isExpired = r.expired;
                const isCritical = !isExpired && r.days_left != null && r.days_left <= 14;
                return (
                  <tr key={r.id} data-testid={`challenge-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.subject}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-md" title={r.description}>{r.description}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium">{TARGET_LABEL[r.target_type] || r.target_type}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{r.target_id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums">{r.filed_at?.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.deadline_at ? (
                        <div>
                          <div className="tabular-nums">{r.deadline_at.slice(0, 10)}</div>
                          {r.days_left != null && (
                            <div className={`text-[10px] ${isExpired ? "text-rose-700 font-medium" : isCritical ? "text-amber-700 font-medium" : "text-slate-500"}`}>
                              {isExpired ? `abgelaufen (${Math.abs(r.days_left)} T.)` : `noch ${r.days_left} Tage`}
                            </div>
                          )}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${s.cls}`}>
                        <Icon size={11} /> {s.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        r.source?.startsWith("portal") ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"}`}>
                        {r.source?.startsWith("portal") ? "Portal" : "Intern"}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(r)}
                              data-testid={`challenge-edit-${r.id}`}>Bearbeiten</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ChallengeEditDialog
          challenge={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ChallengeEditDialog({ challenge, onClose, onSaved }) {
  const [status, setStatus] = useState(challenge.status);
  const [notes, setNotes] = useState(challenge.resolution_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/challenges/${challenge.id}`, {
        status, resolution_notes: notes,
      });
      toast.success("Aktualisiert");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="challenge-edit-dialog">
        <DialogHeader>
          <DialogTitle>Widerspruch bearbeiten</DialogTitle>
          <DialogDescription>{challenge.subject}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">
            {challenge.description}
          </div>
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="challenge-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Bearbeitungsnotizen</label>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="challenge-notes" />
          </div>
          {challenge.linked_ticket_id && (
            <div className="text-[10px] text-slate-500">
              Verknüpftes Ticket: <a href={`/tickets/${challenge.linked_ticket_id}`} className="underline">öffnen</a>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="challenge-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
